import { LocationOn as LocationIcon } from '@mui/icons-material'
import { Avatar, Box, Card, CardContent, Grid, Typography } from '@mui/material'
// biome-ignore lint/style/useImportType: <This is not a type-only import>
import React from 'react'
import type { Pagination } from '../../interfaces/index'
import { formatLocationString } from '../../lib/utils/formatLocationString'
import type { SearchPerkResult } from '../../types'
import PerkBadges from '../shared/PerkBadges'
import { BadgeLayout } from '../shared/types/badge.types'
import PerkSuggestionBanner from './PerkSuggestionBanner'

interface PerksResultsTypesenseProps {
  results?: SearchPerkResult[]
  toggleModal?: (perk: SearchPerkResult) => Promise<void>
  pagination?: Pagination
}

const PerksResultsTypesense: React.FC<PerksResultsTypesenseProps> = ({
  results = [],
  toggleModal,
}) => {
  const formatValue = (value: number, currency = 'USD') => {
    if (!value) return null
    return `$${value.toLocaleString()} ${currency}`
  }

  const getCompanyInitial = (companyName: string) => {
    return companyName?.charAt(0)?.toUpperCase() || '?'
  }

  return (
    <Box>
      {results && results.length > 0 ? (
        <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
          {results.map((perk) => (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              key={perk.id}
              sx={{ display: 'flex' }}
            >
              <Card
                component="button"
                onClick={() => toggleModal?.(perk)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggleModal?.(perk)
                  }
                }}
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'left',
                  transition: 'all 0.2s ease-in-out',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                  },
                  '&:focus': {
                    outline: '2px solid #1976d2',
                    outlineOffset: '2px',
                  },
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }}
                >
                  {/* Company Logo and Name */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={perk.logoUrl || undefined}
                      alt={perk.companyName}
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: perk.logoUrl ? 'transparent' : '#000',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        mr: 2,
                        color: perk.logoUrl ? 'inherit' : '#fff',
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      {!perk.logoUrl && getCompanyInitial(perk.companyName)}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: '18px',
                          lineHeight: 1.2,
                          mb: 0.5,
                        }}
                      >
                        {perk.companyName}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '13px' }}
                      >
                        {perk.categories?.slice(0, 2).join(', ')}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Location */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <LocationIcon
                      sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '13px' }}
                    >
                      Available In {formatLocationString(perk.locations)}
                    </Typography>
                  </Box>

                  {/* Perk Description */}
                  <Typography
                    variant="body1"
                    color="text.primary"
                    sx={{
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      fontSize: '14px',
                      lineHeight: 1.5,
                      fontWeight: 400,
                    }}
                  >
                    {perk.perkShortDesc}
                  </Typography>

                  {/* Value */}
                  <Typography
                    variant="h6"
                    color="text.primary"
                    sx={{
                      mb: 2,
                      fontSize: '16px',
                      color: 'rgba(0, 0, 0, 0.6)',
                    }}
                  >
                    {perk.value
                      ? `${formatValue(perk.value, perk.valueCurrency)} Value`
                      : null}
                  </Typography>
                </CardContent>

                {/* Categories + Highlights */}
                {(perk.isTechstarsCompany || perk.isGlobalNetworkPartner) && (
                  <Box sx={{ px: 3, pb: 3 }}>
                    <PerkBadges
                      isTechstarsCompany={perk.isTechstarsCompany}
                      isGlobalNetworkPartner={perk.isGlobalNetworkPartner}
                      layout={BadgeLayout.VERTICAL}
                    />
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
          <PerkSuggestionBanner />
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No perks found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria or filters
          </Typography>
          <PerkSuggestionBanner />
        </Box>
      )}
    </Box>
  )
}

export default PerksResultsTypesense
